import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createCommunityCommentThunk,
  deleteCommunityCommentThunk,
  ensureDirectMessageThreadThunk,
  fetchCommunityCommentsThunk,
  followCommunityAuthorThunk,
  likeCommunityCommentThunk,
  likeCommunityPostThunk,
  toggleSavePost,
  unfollowCommunityAuthorThunk,
  unlikeCommunityCommentThunk,
  unlikeCommunityPostThunk,
} from '../../redux/communitySlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import type { AsyncStatus } from '../../redux/communitySlice';
import type { CommunityPost, CommunityTopic } from '../../types';
import { fetchCommunityPosts } from '../../services/communityService';
import CommunityPostCard from '../../components/CommunityPostCard';
import './ProfilePage.css';

type LoaderStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type AuthorMetrics = {
  posts: number;
  likes: number;
  comments: number;
  topTopics: { label: string; count: number }[];
};

const buildAuthorMetrics = (posts: CommunityPost[]): AuthorMetrics => {
  const accumulator: AuthorMetrics = {
    posts: posts.length,
    likes: 0,
    comments: 0,
    topTopics: [],
  };
  const topicCounter = new Map<string, number>();

  posts.forEach((post) => {
    accumulator.likes += post.stats?.likes ?? 0;
    accumulator.comments += post.stats?.comments ?? 0;
    const topics = post.topic_details?.length
      ? post.topic_details.map((topic) => topic.label)
      : post.topics;
    topics.forEach((label) => {
      topicCounter.set(label, (topicCounter.get(label) ?? 0) + 1);
    });
  });

  accumulator.topTopics = Array.from(topicCounter.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([label, count]) => ({ label, count }));

  return accumulator;
};

const UserProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { authorId } = useParams<{ authorId: string }>();
  const numericAuthorId = authorId ? Number(authorId) : NaN;

  const { user } = useAppSelector((state) => state.auth);
  const { comments, topics, savedPostIds } = useAppSelector((state) => state.community);

  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [status, setStatus] = useState<LoaderStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [likePendingMap, setLikePendingMap] = useState<Record<string, boolean>>({});
  const [followPending, setFollowPending] = useState(false);
  const [dmPending, setDmPending] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(numericAuthorId)) {
      setStatus('failed');
      setError('未找到该用户');
      return;
    }
    setStatus('loading');
    setError(null);
    fetchCommunityPosts({ author_id: numericAuthorId, sort: 'latest', limit: 30 })
      .then((response) => {
        setPosts(response.items);
        setStatus('succeeded');
      })
      .catch((fetchError: unknown) => {
        const message = fetchError instanceof Error ? fetchError.message : '加载用户内容失败，请稍后再试。';
        setError(message);
        setStatus('failed');
      });
  }, [numericAuthorId]);

  const topicsDict = useMemo(() => {
    return topics.reduce<Record<string, CommunityTopic>>((acc, topic) => {
      acc[topic.id] = topic;
      return acc;
    }, {});
  }, [topics]);

  const authorSummary = useMemo(() => {
    const sample = posts[0]?.author;
    return {
      id: sample?.id ?? numericAuthorId,
      name: sample?.name || sample?.username || '社区用户',
      username: sample?.username ?? null,
      avatar: sample?.avatar_url ?? null,
    };
  }, [posts, numericAuthorId]);

  const metrics = useMemo(() => buildAuthorMetrics(posts), [posts]);

  const isSelf = user?.id === authorSummary.id;
  const isFollowing = useMemo(() => {
    if (isSelf) {
      return false;
    }
    return posts.some((post) => post.viewer?.follows_author);
  }, [isSelf, posts]);

  const handleFollowToggle = async () => {
    if (isSelf || !authorSummary.id || followPending) {
      return;
    }
    setFollowPending(true);
    try {
      if (isFollowing) {
        await dispatch(unfollowCommunityAuthorThunk(authorSummary.id)).unwrap();
      } else {
        await dispatch(followCommunityAuthorThunk(authorSummary.id)).unwrap();
      }
      setPosts((prev) =>
        prev.map((post) => ({
          ...post,
          viewer: {
            ...(post.viewer ?? {}),
            follows_author: !isFollowing,
          },
        })),
      );
    } catch (toggleError) {
      const message = toggleError instanceof Error ? toggleError.message : '操作失败，请稍后再试';
      window.alert(message);
    } finally {
      setFollowPending(false);
    }
  };

  const handleStartDirectMessage = async () => {
    if (isSelf || !authorSummary.id || dmPending) {
      return;
    }
    setDmPending(true);
    try {
      const thread = await dispatch(ensureDirectMessageThreadThunk(authorSummary.id)).unwrap();
  navigate(`/app/community/messages/${thread.thread_id}`);
    } catch (startError) {
      const message = startError instanceof Error ? startError.message : '无法发起私信，请稍后再试。';
      window.alert(message);
    } finally {
      setDmPending(false);
    }
  };

  const handleOpenPost = (postId: string) => {
  navigate(`/app/community/status/${postId}`);
  };

  const handleToggleSave = (post: CommunityPost) => {
    const nextSaved = !(post.viewer?.has_saved ?? savedPostIds.includes(post.post_id));
    dispatch(toggleSavePost(post));
    setPosts((prev) =>
      prev.map((item) =>
        item.post_id === post.post_id
          ? {
              ...item,
              viewer: {
                ...(item.viewer ?? {}),
                has_saved: nextSaved,
              },
            }
          : item,
      ),
    );
  };

  const handleToggleLike = async (postId: string, hasLiked: boolean) => {
    if (likePendingMap[postId]) {
      return;
    }
    setLikePendingMap((prev) => ({ ...prev, [postId]: true }));
    try {
      const updated = hasLiked
        ? await dispatch(unlikeCommunityPostThunk(postId)).unwrap()
        : await dispatch(likeCommunityPostThunk(postId)).unwrap();
      setPosts((prev) => prev.map((item) => (item.post_id === updated.post_id ? updated : item)));
    } catch (likeError) {
      const message = likeError instanceof Error ? likeError.message : '操作失败，请稍后再试';
      window.alert(message);
    } finally {
      setLikePendingMap((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    }
  };

  const handleLoadComments = (postId: string) => {
    const entry = comments[postId];
    if (!entry || entry.status === 'idle' || entry.status === 'failed') {
      dispatch(fetchCommunityCommentsThunk({ postId }));
    }
  };

  const handleSubmitComment = async (postId: string, content: string, parentCommentId?: string | null) => {
    await dispatch(
      createCommunityCommentThunk({
        postId,
        payload: {
          content,
          parent_comment_id: parentCommentId ?? undefined,
        },
      }),
    ).unwrap();
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    await dispatch(deleteCommunityCommentThunk({ postId, commentId })).unwrap();
  };

  const handleToggleCommentLike = async (postId: string, commentId: string, hasLiked: boolean) => {
    const thunk = hasLiked ? unlikeCommunityCommentThunk : likeCommunityCommentThunk;
    await dispatch(thunk({ postId, commentId })).unwrap();
  };

  if (!Number.isFinite(numericAuthorId)) {
    return <div className="community-profile__placeholder">该作者信息不存在。</div>;
  }

  return (
    <div className="community-profile">
      <header>
        <div className="community-profile__avatar">
          {authorSummary.avatar ? (
            <img src={authorSummary.avatar} alt={authorSummary.name} />
          ) : (
            <span>{authorSummary.name.slice(0, 1)}</span>
          )}
        </div>
        <div className="community-profile__intro">
          <h1>{authorSummary.name}</h1>
          <p>{isSelf ? '这里展示你的公开社区动态。' : '浏览该创作者在草本社区发布的精选内容。'}</p>
          {!isSelf && (
            <div className="community-profile__actions">
              <button type="button" onClick={handleFollowToggle} disabled={followPending}>
                {followPending ? '处理中…' : isFollowing ? '已关注' : '关注创作者'}
              </button>
              <button
                type="button"
                className="community-profile__dm-btn"
                onClick={handleStartDirectMessage}
                disabled={dmPending}
              >
                {dmPending ? '唤起中…' : '发起私信'}
              </button>
            </div>
          )}
        </div>
      </header>

      <section className="community-profile__stats">
        <div>
          <span>内容总数</span>
          <strong>{metrics.posts}</strong>
        </div>
        <div>
          <span>累计获赞</span>
          <strong>{metrics.likes}</strong>
        </div>
        <div>
          <span>互动次数</span>
          <strong>{metrics.comments}</strong>
        </div>
      </section>

      {metrics.topTopics.length > 0 && (
        <section className="community-profile__topics">
          <h2>热门话题</h2>
          <ul>
            {metrics.topTopics.map((topic) => (
              <li key={topic.label}>
                <span>#{topic.label}</span>
                <small>{topic.count} 篇内容</small>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="community-profile__feed">
        <div className="community-profile__feed-header">
          <h2>{isSelf ? '我的社区动态' : 'TA 的最新帖子'}</h2>
        </div>

        {status === 'loading' && <div className="community-profile__placeholder">内容加载中...</div>}
        {status === 'failed' && error && <div className="community-profile__error">{error}</div>}

        {status === 'succeeded' && posts.length === 0 && (
          <div className="community-profile__placeholder">该创作者暂未发布公开内容。</div>
        )}

        {posts.map((post) => {
          const commentEntry = comments[post.post_id];
          const commentList = commentEntry?.items ?? [];
          const commentStatus: AsyncStatus = commentEntry?.status ?? 'idle';
          const commentError = commentEntry?.error ?? null;
          const commentCreateStatus: AsyncStatus = commentEntry?.createStatus ?? 'idle';
          const commentCreateError = commentEntry?.createError ?? null;
          const commentLikePending = commentEntry?.likePending ?? {};
          const viewer = post.viewer ?? {};
          const hasSaved = viewer.has_saved ?? savedPostIds.includes(post.post_id);

          return (
            <CommunityPostCard
              key={post.post_id}
              post={post}
              topicsDict={topicsDict}
              comments={commentList}
              commentsStatus={commentStatus}
              commentsError={commentError}
              createStatus={commentCreateStatus}
              createError={commentCreateError}
              onLoadComments={() => handleLoadComments(post.post_id)}
              onSubmitComment={(content, parentId) => handleSubmitComment(post.post_id, content, parentId)}
              onDeleteComment={(commentId) => handleDeleteComment(post.post_id, commentId)}
              onToggleCommentLike={(commentId, hasLiked) => handleToggleCommentLike(post.post_id, commentId, hasLiked)}
              commentLikePending={commentLikePending}
              currentUserId={user?.id ?? null}
              onToggleLike={() => handleToggleLike(post.post_id, viewer.has_liked ?? false)}
              likePending={likePendingMap[post.post_id]}
              onToggleFollow={authorSummary.id ? handleFollowToggle : undefined}
              followPending={followPending}
              hasSaved={hasSaved}
              onToggleSave={() => handleToggleSave(post)}
              onOpenPost={() => handleOpenPost(post.post_id)}
              onOpenAuthor={authorSummary.id ? () => navigate(`/app/community/users/${authorSummary.id}`) : undefined}
              onOpenRepost={handleOpenPost}
            />
          );
        })}
      </section>
    </div>
  );
};

export default UserProfilePage;
